import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, GridLegacy as Grid, Typography } from '@mui/material';
import {
  Add,
  Assignment,
  ContentCopy,
  Dashboard as DashboardIcon,
  Groups,
  HowToReg,
  People,
  PendingActions,
} from '@mui/icons-material';
import { useAuth } from '../utils/AuthContext';
import { managerAPI } from '../services/api';
import AppShell from '../components/shared/AppShell';
import { Section, SectionHeader, StatCard } from '../components/shared/ui';
import RequirementList from '../components/manager/RequirementList';
import RequirementCandidates from '../components/manager/RequirementCandidates';
import RequirementForm from '../components/manager/RequirementForm';
import { tokens } from '../theme';

// One source of truth for navigation. The indices below must match the
// `tab === n` blocks in the render — the shell builds both the desktop rail and
// the mobile drawer from this array.
const SECTIONS = [
  { label: 'Overview', icon: <DashboardIcon /> },
  { label: 'My Requirements', icon: <Assignment /> },
  { label: 'Candidates', icon: <People /> },
];

const ManagerDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focusedRequirement, setFocusedRequirement] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await managerAPI.getDashboard();
      setStats(response.data?.statistics || null);
    } catch (e) {
      setError(
        e.response?.status === 401
          ? 'Please sign in as a hiring manager to view this dashboard.'
          : e.response?.data?.error || 'Could not load your dashboard.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'manager') loadStats();
  }, [isAuthenticated, user, loadStats]);

  const openCandidatesFor = (requirement) => {
    setFocusedRequirement(requirement);
    setTab(2);
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={28} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Loading TalentMesh…</Typography>
        </Box>
      </Box>
    );
  }

  if (user?.role !== 'manager') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: tokens.color.danger }}>
          Access denied — hiring manager role required
        </Typography>
      </Box>
    );
  }

  return (
    <AppShell
      role="manager"
      sections={SECTIONS}
      activeIndex={tab}
      onSectionChange={(index) => {
        // Leaving the candidates tab drops the requirement focus, so returning
        // to it shows everything rather than a stale filter.
        if (index !== 2) setFocusedRequirement(null);
        setTab(index);
      }}
      onRefresh={loadStats}
      loading={loading}
      error={error}
      actions={[
        {
          label: 'New requirement',
          icon: <Add />,
          variant: 'contained',
          onClick: () => setFormOpen(true),
        },
      ]}
    >
      {tab === 0 && (
        <>
          <SectionHeader
            title={`Welcome, ${user?.username || 'there'}`}
            subtitle="Raise a requirement for the expertise you need, and track what vendor/HR send back."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Open requirements"
                value={(stats?.requirements_open || 0) + (stats?.requirements_in_sourcing || 0)}
                caption={`${stats?.open_headcount || 0} position${stats?.open_headcount === 1 ? '' : 's'} to fill`}
                icon={<Assignment />}
                tone="info"
                onClick={() => setTab(1)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Drafts"
                value={stats?.requirements_draft || 0}
                caption="Not yet sent to vendor/HR"
                icon={<PendingActions />}
                tone="warning"
                onClick={() => setTab(1)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Candidates received"
                value={stats?.candidates_total || 0}
                caption={`${stats?.candidates_active || 0} still in progress`}
                icon={<Groups />}
                tone="success"
                onClick={() => setTab(2)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Flagged duplicates"
                value={stats?.candidates_flagged_duplicate || 0}
                caption="Submitted more than once"
                icon={<ContentCopy />}
                tone={stats?.candidates_flagged_duplicate ? 'warning' : 'neutral'}
                onClick={() => setTab(2)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Hired"
                value={stats?.candidates_hired || 0}
                caption="Across all requirements"
                icon={<HowToReg />}
                tone="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Fulfilled requirements"
                value={stats?.requirements_fulfilled || 0}
                caption="Closed with a hire"
                icon={<HowToReg />}
                tone="success"
                onClick={() => setTab(1)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Closed"
                value={stats?.requirements_closed || 0}
                caption="Closed or cancelled"
                icon={<Assignment />}
                tone="neutral"
                onClick={() => setTab(1)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total raised"
                value={stats?.requirements_total || 0}
                caption="All time"
                icon={<Assignment />}
                tone="neutral"
                onClick={() => setTab(1)}
              />
            </Grid>
          </Grid>

          <Section
            title="How this works"
            subtitle="From a stated need to a candidate in the pipeline"
          >
            <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.75 }}>
              {[
                'Raise a requirement: the role and expertise you need, the experience band, and how many people.',
                'Publish it. Every active vendor/HR user sees it in their sourcing queue.',
                'A recruiter claims it and uploads resumes. Each resume is read automatically and the candidate details are filled in for them.',
                'If someone else already submitted the same person, that submission is flagged — you can see who got there first.',
                'Track everything under Candidates, and mark the requirement fulfilled when you have your hires.',
              ].map((step) => (
                <Typography component="li" variant="body1" key={step}>
                  {step}
                </Typography>
              ))}
            </Box>
          </Section>
        </>
      )}

      {tab === 1 && (
        <RequirementList onViewCandidates={openCandidatesFor} onChanged={loadStats} />
      )}

      {tab === 2 && (
        <RequirementCandidates
          requirement={focusedRequirement}
          onBack={focusedRequirement ? () => setFocusedRequirement(null) : undefined}
          onChanged={loadStats}
        />
      )}

      <RequirementForm
        open={formOpen}
        requirement={null}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          loadStats();
          setTab(1);
        }}
      />
    </AppShell>
  );
};

export default ManagerDashboard;
